import type { ComponentProps } from 'react';
import { animated, type SpringValues } from '@react-spring/web';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { Box } from '@mentimeter/ragnar-ui/box';
import { List } from '@mentimeter/ragnar-ui/list';
import { Text } from '@mentimeter/ragnar-ui/text';
import type { TextT } from '@mentimeter/ragnar-ui/text';
import { useMentiSpring } from '@mentimeter/motion/spring';

export const Legend = ({ children, ...rest }: ComponentProps<typeof List>) => {
  return (
    <List
      justifyContent="center"
      extend={({ theme }) => ({
        '>:not(:last-child)': {
          paddingBottom: `${theme.kosmosSpacing.space2}px`,
        },
      })}
      {...rest}
    >
      {children}
    </List>
  );
};

export interface ItemProps extends BoxT {
  opacity?: number;
  children: React.ReactNode;
}

const Item = ({
  opacity = 1,
  onMouseEnter,
  onMouseLeave,
  children,
  ...rest
}: ItemProps) => {
  const [fadeOut] = useMentiSpring({
    from: { opacity: 1 },
    to: { opacity },
  });

  return (
    <Box
      as={animated.li}
      width="100%"
      flexDirection="row"
      alignItems="flex-start"
      gap="space4"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={fadeOut as SpringValues}
      {...rest}
    >
      {children}
    </Box>
  );
};

const ItemDivider = () => (
  <Box
    m="0"
    width="100%"
    borderStyle="solid"
    borderColor="border"
    borderTopWidth="1px"
  />
);

const ItemLabel = ({
  fontSize = 'inherit',
  children,
}: React.PropsWithChildren<{ fontSize?: TextT['fontSize'] }>) => (
  <Text fontSize={fontSize}>{children}</Text>
);

const ItemMarker = ({ color = 'inherit' }: { color?: string }) => (
  // We place the marker in a span with display: inline-block to align it with the baseline of the rest of the text
  <Text fontSize="inherit">
    <Box
      display="inline-block"
      height="0.9em"
      maxHeight="24px"
      borderRadius="30%"
      bg={color}
      style={{ verticalAlign: 'middle', aspectRatio: '1 / 1' }}
    />
  </Text>
);

Legend.Item = Item;
Legend.ItemLabel = ItemLabel;
Legend.ItemMarker = ItemMarker;
Legend.ItemDivider = ItemDivider;
